/**
 * StatusSummaryWidget — displays overall platform status.
 */

import { useEffect, useState } from "react";
import { apiFetch } from "../../../lib/config";

interface StatusSummary {
  overall_status: string;
  degraded_count: number;
  outage_count: number;
}

interface Props {
  config: Record<string, unknown>;
}

export function StatusSummaryWidget({ config: _config }: Props) {
  const [summary, setSummary] = useState<StatusSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch("/api/v1/status/summary/")
      .then((r) => r.json())
      .then((d) => setSummary(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="aptean-widget-skeleton" style={{ height: 60 }} />;
  if (error) return <p style={{ color: "var(--aptean-error, #e74c3c)", margin: 0 }}>Error: {error}</p>;

  const statusColor =
    summary?.overall_status === "operational"
      ? "var(--aptean-success, #27ae60)"
      : summary?.overall_status === "degraded"
        ? "var(--aptean-warning, #f39c12)"
        : "var(--aptean-error, #e74c3c)";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: statusColor,
          flexShrink: 0,
        }}
        aria-hidden="true"
      />
      <span style={{ color: "var(--aptean-text-primary)", fontWeight: 600, textTransform: "capitalize" }}>
        {summary?.overall_status ?? "Unknown"}
      </span>
      {summary && (summary.degraded_count > 0 || summary.outage_count > 0) && (
        <span style={{ fontSize: "0.8rem", color: "var(--aptean-text-secondary)" }}>
          ({summary.degraded_count} degraded, {summary.outage_count} outage)
        </span>
      )}
    </div>
  );
}
